import React, { useRef } from 'react';
import { motion, useMotionValue, useSpring, useReducedMotion } from 'framer-motion';
import useMediaQuery from '../hooks/useMediaQuery';
import './TiltCard.css';

// Pointer-tracked 3D tilt + cursor glow. The glow position is written as CSS
// vars straight onto the DOM node so pointer moves never re-render React.
// On touch devices / reduced motion it renders as a plain motion element.
const TiltCard = ({ children, className = '', maxTilt = 6, ...motionProps }) => {
  const ref = useRef(null);
  const rectRef = useRef(null);
  const finePointer = useMediaQuery('(pointer: fine)');
  const reducedMotion = useReducedMotion();
  const tiltOn = finePointer && !reducedMotion;

  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 250, damping: 22 });
  const sry = useSpring(ry, { stiffness: 250, damping: 22 });

  const onPointerEnter = () => {
    rectRef.current = ref.current.getBoundingClientRect();
  };

  const onPointerMove = (e) => {
    const rect = rectRef.current;
    if (!rect) return;
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    ry.set((px - 0.5) * 2 * maxTilt);
    rx.set(-(py - 0.5) * 2 * maxTilt);
    ref.current.style.setProperty('--mx', `${e.clientX - rect.left}px`);
    ref.current.style.setProperty('--my', `${e.clientY - rect.top}px`);
  };

  const onPointerLeave = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <motion.article
      ref={ref}
      className={`tilt-card ${className}`}
      style={tiltOn ? { rotateX: srx, rotateY: sry, transformPerspective: 800 } : undefined}
      whileTap={{ scale: 0.98 }}
      {...(tiltOn ? { onPointerEnter, onPointerMove, onPointerLeave } : {})}
      {...motionProps}
    >
      {children}
    </motion.article>
  );
};

export default TiltCard;
